import { useEffect, useState } from 'react';
import { getNotificationCounts } from '../services/notificationService';

const POLL_INTERVAL_MS = 30000;
const EMPTY_COUNTS = { all: 0, schedule: 0, timesheets: 0 };

let latestCounts = EMPTY_COUNTS;
const subscribers = new Set();
let intervalId = null;
let inFlight = false;
let visibilityListenerAttached = false;

const notifySubscribers = () => {
  subscribers.forEach((cb) => cb(latestCounts));
};

const fetchCounts = async () => {
  if (inFlight) return;
  inFlight = true;
  try {
    const counts = await getNotificationCounts();
    latestCounts = counts || EMPTY_COUNTS;
    notifySubscribers();
  } catch {
    // Keep last-known counts on failure.
  } finally {
    inFlight = false;
  }
};

const startPolling = () => {
  if (intervalId !== null) return;
  if (typeof document !== 'undefined' && document.hidden) return;
  intervalId = setInterval(fetchCounts, POLL_INTERVAL_MS);
};

const stopPolling = () => {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const handleVisibilityChange = () => {
  if (typeof document === 'undefined') return;
  if (document.hidden) {
    stopPolling();
  } else if (subscribers.size > 0) {
    fetchCounts();
    startPolling();
  }
};

const attachVisibilityListener = () => {
  if (visibilityListenerAttached || typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAttached = true;
};

const detachVisibilityListener = () => {
  if (!visibilityListenerAttached || typeof document === 'undefined') return;
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAttached = false;
};

export function refreshNotificationCounts() {
  return fetchCounts();
}

export default function useNotificationCounts() {
  const [counts, setCounts] = useState(latestCounts);

  useEffect(() => {
    const callback = (next) => setCounts(next);
    const wasEmpty = subscribers.size === 0;
    subscribers.add(callback);

    if (wasEmpty) {
      attachVisibilityListener();
      fetchCounts();
      startPolling();
    } else {
      setCounts(latestCounts);
    }

    return () => {
      subscribers.delete(callback);
      if (subscribers.size === 0) {
        stopPolling();
        detachVisibilityListener();
      }
    };
  }, []);

  return counts;
}
